import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Form, Offcanvas } from 'react-bootstrap';
import {
  INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS,
  type InternalSubtaskTableColumnId,
  type InternalSubtaskTableColumnOption,
  reorderInternalSubtaskTableColumns,
  toggleInternalSubtaskTableColumn,
} from '../changeInternalSubtaskTableColumns';
import './ChangeInternalSubtaskColumnConfigurator.css';

interface SortableRowProps {
  option: InternalSubtaskTableColumnOption;
}

const SortableColumnRow: React.FC<SortableRowProps> = ({ option }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.45 : 1,
      }}
      className="change-internal-subtask-column-row"
    >
      <button
        type="button"
        className="btn btn-light btn-sm change-internal-subtask-column-handle"
        aria-label={`Reordenar coluna ${option.label}`}
        title="Segure e arraste para reordenar"
        {...attributes}
        {...listeners}
      >
        ⋮⋮
      </button>
      <div className="flex-grow-1">
        <div className="fw-semibold small">{option.label}</div>
        {option.hint ? <div className="text-muted small">{option.hint}</div> : null}
      </div>
      {option.group === 'core' ? (
        <Badge bg="secondary" className="text-nowrap">padrão</Badge>
      ) : (
        <Badge bg="info" className="text-nowrap">checklist</Badge>
      )}
    </div>
  );
};

interface Props {
  show: boolean;
  onHide: () => void;
  catalog: InternalSubtaskTableColumnOption[];
  selected: InternalSubtaskTableColumnId[];
  catalogIds: string[];
  onChange: (next: InternalSubtaskTableColumnId[]) => void;
}

const ChangeInternalSubtaskColumnConfigurator: React.FC<Props> = ({
  show,
  onHide,
  catalog,
  selected,
  catalogIds,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedOptions = useMemo(
    () =>
      selected
        .map((id) => catalog.find((item) => item.id === id))
        .filter((item): item is InternalSubtaskTableColumnOption => Boolean(item)),
    [catalog, selected],
  );

  const checklistCatalog = useMemo(
    () => catalog.filter((item) => item.group === 'checklist'),
    [catalog],
  );

  const filteredChecklist = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return checklistCatalog;
    return checklistCatalog.filter(
      (item) =>
        item.label.toLowerCase().includes(term) || String(item.id).toLowerCase().includes(term),
    );
  }, [checklistCatalog, query]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;
    onChange(
      reorderInternalSubtaskTableColumns(
        selected,
        String(active.id),
        String(over.id),
        catalogIds,
      ),
    );
  };

  const activeOption = activeId ? catalog.find((item) => item.id === activeId) : null;

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      className="change-internal-subtask-column-offcanvas"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Colunas das subtarefas internas</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <p className="text-muted small">
          Escolha o que aparece na tabela e arraste para definir a ordem. Campos de checklist vêm dos
          modelos usados nesta mudança; se uma subtarefa não tiver o campo, a célula mostra —.
        </p>

        <div className="fw-semibold small mb-1">Ordem das colunas visíveis</div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={(event) => setActiveId(String(event.active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveId(null)}
        >
          <SortableContext
            items={selectedOptions.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            {selectedOptions.map((option) => (
              <SortableColumnRow key={option.id} option={option} />
            ))}
          </SortableContext>
          <DragOverlay>
            {activeOption ? (
              <div className="change-internal-subtask-column-row shadow-sm">
                <span className="change-internal-subtask-column-handle">⋮⋮</span>
                <span className="fw-semibold small">{activeOption.label}</span>
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>

        <div className="fw-semibold small mt-4 mb-1">Colunas padrão</div>
        <div className="change-internal-subtask-column-checks mb-3">
          {INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS.map((option) => {
            const checked = selectedSet.has(option.id);
            const onlyCore =
              selected.filter((id) => INTERNAL_SUBTASK_CORE_COLUMN_OPTIONS.some((c) => c.id === id))
                .length <= 1;
            return (
              <Form.Check
                key={option.id}
                type="checkbox"
                id={`internal-subtask-col-${option.id}`}
                label={option.label}
                checked={checked}
                disabled={onlyCore && checked}
                onChange={() =>
                  onChange(toggleInternalSubtaskTableColumn(selected, option.id, catalogIds))
                }
              />
            );
          })}
        </div>

        {checklistCatalog.length ? (
          <>
            <div className="fw-semibold small mb-1">Campos do checklist</div>
            <Form.Control
              type="search"
              size="sm"
              className="mb-2"
              placeholder="Buscar campo..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              autoComplete="off"
            />
            <div className="change-internal-subtask-column-checks">
              {filteredChecklist.map((option) => (
                <Form.Check
                  key={option.id}
                  type="checkbox"
                  id={`internal-subtask-col-${option.id}`}
                  label={option.label}
                  checked={selectedSet.has(option.id)}
                  onChange={() =>
                    onChange(toggleInternalSubtaskTableColumn(selected, option.id, catalogIds))
                  }
                />
              ))}
              {!filteredChecklist.length ? (
                <div className="small text-muted">Nenhum campo encontrado.</div>
              ) : null}
            </div>
          </>
        ) : (
          <p className="small text-muted mb-0">
            Nenhum campo de checklist nas subtarefas internas desta mudança. Adicione campos no
            cadastro de subtarefas internas ou na instância ao criar a mudança.
          </p>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ChangeInternalSubtaskColumnConfigurator;
